import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, MessageCircle } from "lucide-react";
import { COMPANY, LOGO, NAV, waLink } from "@/lib/site";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <>
      <div className="hazard-stripe h-[3px] w-full fixed top-0 left-0 z-[60]" />
      <motion.header
        data-testid="site-header"
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed top-[3px] left-0 right-0 z-50 transition-colors duration-500 ${
          scrolled || open ? "bg-black/88 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
        }`}
      >
        <div className="w-full px-4 sm:px-8 lg:px-12">
          <div className="flex items-center justify-between h-[62px] sm:h-[74px] gap-3">
            <Link to="/" data-testid="logo-link" className="flex items-center shrink-0">
              <img src={LOGO} alt="Global Move Europa" className="h-9 sm:h-[50px] w-auto" />
            </Link>

            <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
              {NAV.map((n) => (
                <a
                  key={n.href}
                  href={n.href}
                  data-testid={`nav-${n.href.split("#")[1]}`}
                  className="link-underline font-display text-[15px] font-semibold uppercase tracking-wider text-neutral-200 hover:text-white transition-colors"
                >
                  {n.label}
                </a>
              ))}
            </nav>

            <div className="flex items-center gap-2 sm:gap-3">
              <a
                href={`tel:${COMPANY.phoneRaw}`}
                data-testid="header-phone-link"
                className="hidden xl:flex items-center gap-2 border border-white/20 px-4 h-11 text-neutral-200 hover:border-[#f95700] hover:text-white transition-colors"
              >
                <Phone size={15} className="text-[#f95700]" />
                <span className="font-mono-t text-[13px]">{COMPANY.phoneDisplay}</span>
              </a>
              <a
                href={waLink("Hallo Global Move Europa, ich habe eine Frage zu einem Transport.")}
                target="_blank"
                rel="noreferrer"
                data-testid="header-whatsapp-button"
                aria-label="WhatsApp kontaktieren"
                className="flex items-center gap-2 bg-[#f95700] hover:bg-[#e04e00] h-10 sm:h-11 w-10 sm:w-auto justify-center sm:px-5 font-display font-bold uppercase tracking-wide text-white transition-colors"
              >
                <MessageCircle size={18} />
                <span className="hidden sm:inline">WhatsApp</span>
              </a>
              <button
                onClick={() => setOpen((v) => !v)}
                data-testid="mobile-menu-toggle"
                aria-label="Menü öffnen"
                className="lg:hidden w-10 h-10 sm:w-11 sm:h-11 grid place-items-center border border-white/20 text-white"
              >
                {open ? <X size={19} /> : <Menu size={19} />}
              </button>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="lg:hidden overflow-hidden bg-black/96 backdrop-blur-xl border-t border-white/10"
              data-testid="mobile-menu"
            >
              <div className="px-5 py-5 flex flex-col">
                {NAV.map((n, i) => (
                  <a
                    key={n.href}
                    href={n.href}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${n.href.split("#")[1]}`}
                    className="font-display text-[26px] uppercase font-bold py-2.5 text-neutral-200 active:text-[#f95700] hover:text-[#f95700] transition-colors flex items-baseline gap-3 border-b border-white/8"
                  >
                    <span className="font-mono-t text-[10px] text-[#f95700]">0{i + 1}</span>
                    {n.label}
                  </a>
                ))}
                <a
                  href={`tel:${COMPANY.phoneRaw}`}
                  data-testid="mobile-phone-link"
                  className="mt-5 flex items-center justify-center gap-2 h-14 border border-white/20 font-display text-lg font-bold uppercase text-white"
                >
                  <Phone size={17} className="text-[#f95700]" /> {COMPANY.phoneDisplay}
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>
    </>
  );
};
